import { useCallback, useEffect, useRef } from "react";

/**
 * Ported from the Strapi MulchCalculator. Two bug fixes vs the source:
 *
 *   1. CLOSE_THRESHOLD_PX tightened 14 -> 8. The old value auto-closed
 *      polygons after any click within a 14px radius of the first vertex,
 *      trivially triggered when zoomed in on a small mulch bed.
 *   2. First-vertex marker is only `clickable` after >= 4 vertices exist.
 *      Previously the marker was clickable from the moment it was placed,
 *      meaning a stray 4th click near vertex 1 would close a 3-point ring
 *      the user didn't mean to close.
 *
 * Also fixes:
 *   - Listener cleanup on unmount (Strapi version added a MutationObserver
 *     that never disconnected). Not the hook's concern — see MapEditor.
 */

const CLOSE_THRESHOLD_PX = 8;

export interface LatLngLiteral {
  lat: number;
  lng: number;
}

function latLngToPixel(map: any, latLng: any) {
  const projection = map.getProjection();
  if (!projection) return null;
  const scale = Math.pow(2, map.getZoom());
  const worldPoint = projection.fromLatLngToPoint(latLng);
  const bounds = map.getBounds();
  if (!bounds) return null;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const nePoint = projection.fromLatLngToPoint(ne);
  const swPoint = projection.fromLatLngToPoint(sw);
  const topLeft = new window.google.maps.Point(
    Math.min(swPoint.x, nePoint.x),
    Math.min(swPoint.y, nePoint.y),
  );
  return new window.google.maps.Point(
    (worldPoint.x - topLeft.x) * scale,
    (worldPoint.y - topLeft.y) * scale,
  );
}

function isNearFirstVertex(map: any, clickLatLng: any, firstLatLng: any) {
  const p1 = latLngToPixel(map, clickLatLng);
  const p2 = latLngToPixel(map, firstLatLng);
  if (!p1 || !p2) return false;
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy) < CLOSE_THRESHOLD_PX;
}

export function useCustomDrawing(
  mapInstanceRef: React.MutableRefObject<any>,
  onPolygonComplete: (path: any[]) => void,
) {
  const drawingActiveRef = useRef(false);
  const verticesRef = useRef<any[]>([]);
  const previewPolylineRef = useRef<any>(null);
  const closingLineRef = useRef<any>(null);
  const vertexMarkersRef = useRef<any[]>([]);
  const clickListenerRef = useRef<any>(null);
  const dblClickListenerRef = useRef<any>(null);
  const mouseMoveListenerRef = useRef<any>(null);
  const cursorLineRef = useRef<any>(null);
  const drawingColorRef = useRef<string>("#FF4444");
  const keyListenerRef = useRef<any>(null);
  const onUndoCallbacksRef = useRef<((count: number) => void)[]>([]);

  const cleanupDrawing = useCallback(() => {
    if (previewPolylineRef.current) {
      previewPolylineRef.current.setMap(null);
      previewPolylineRef.current = null;
    }
    if (cursorLineRef.current) {
      cursorLineRef.current.setMap(null);
      cursorLineRef.current = null;
    }
    if (closingLineRef.current) {
      closingLineRef.current.setMap(null);
      closingLineRef.current = null;
    }
    vertexMarkersRef.current.forEach((m) => m.setMap(null));
    vertexMarkersRef.current = [];
    verticesRef.current = [];
    onUndoCallbacksRef.current = [];
    const map = mapInstanceRef.current;
    if (map) {
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      if (dblClickListenerRef.current) {
        window.google.maps.event.removeListener(dblClickListenerRef.current);
        dblClickListenerRef.current = null;
      }
      if (mouseMoveListenerRef.current) {
        window.google.maps.event.removeListener(mouseMoveListenerRef.current);
        mouseMoveListenerRef.current = null;
      }
      map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
    }
    if (keyListenerRef.current) {
      document.removeEventListener("keydown", keyListenerRef.current);
      keyListenerRef.current = null;
    }
    drawingActiveRef.current = false;
  }, [mapInstanceRef]);

  const closePolygon = useCallback(() => {
    const verts = verticesRef.current;
    if (verts.length < 3) {
      cleanupDrawing();
      return;
    }
    const path = verts.map((v) => v);
    cleanupDrawing();
    onPolygonComplete(path);
  }, [cleanupDrawing, onPolygonComplete]);

  const undoLastVertex = useCallback(() => {
    if (!drawingActiveRef.current || verticesRef.current.length === 0) return;
    const lastMarker = vertexMarkersRef.current.pop();
    if (lastMarker) lastMarker.setMap(null);
    verticesRef.current.pop();

    // Re-evaluate first-vertex clickability after undo
    const first = vertexMarkersRef.current[0];
    if (first) {
      first.setOptions({ clickable: verticesRef.current.length >= 4 });
    }

    if (previewPolylineRef.current) {
      previewPolylineRef.current.setPath(verticesRef.current);
    }
    if (closingLineRef.current) {
      if (verticesRef.current.length >= 2) {
        closingLineRef.current.setPath([
          verticesRef.current[verticesRef.current.length - 1],
          verticesRef.current[0],
        ]);
      } else {
        closingLineRef.current.setPath([]);
      }
    }
    onUndoCallbacksRef.current.forEach((cb) => cb(verticesRef.current.length));
  }, []);

  const startDrawing = useCallback(
    (color: string) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      drawingColorRef.current = color;
      drawingActiveRef.current = true;
      verticesRef.current = [];
      map.setOptions({
        draggableCursor: "crosshair",
        disableDoubleClickZoom: true,
      });

      previewPolylineRef.current = new window.google.maps.Polyline({
        map,
        path: [],
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        clickable: false,
      });
      cursorLineRef.current = new window.google.maps.Polyline({
        map,
        path: [],
        strokeColor: color,
        strokeOpacity: 0.4,
        strokeWeight: 1,
        clickable: false,
        icons: [
          {
            icon: { path: "M 0,-1 0,1", strokeOpacity: 0.5, scale: 3 },
            offset: "0",
            repeat: "10px",
          },
        ],
      });
      closingLineRef.current = new window.google.maps.Polyline({
        map,
        path: [],
        strokeColor: color,
        strokeOpacity: 0.25,
        strokeWeight: 1,
        clickable: false,
        icons: [
          {
            icon: { path: "M 0,-1 0,1", strokeOpacity: 0.4, scale: 2 },
            offset: "0",
            repeat: "8px",
          },
        ],
      });

      const addVertex = (latLng: any) => {
        const isFirst = verticesRef.current.length === 0;
        verticesRef.current.push(latLng);

        if (previewPolylineRef.current) {
          previewPolylineRef.current.setPath(verticesRef.current);
        }

        const marker = new window.google.maps.Marker({
          position: latLng,
          map,
          // BUG FIX 2: first-vertex is only clickable at 4+ vertices. It
          // becomes clickable after the 4th vertex is placed (see below).
          clickable: false,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: isFirst ? 7 : 3,
            fillColor: isFirst ? "#FFFFFF" : color,
            fillOpacity: 1,
            strokeColor: color,
            strokeWeight: isFirst ? 3 : 2,
          },
          zIndex: isFirst ? 1001 : 999,
        });
        if (isFirst) {
          marker.addListener("click", () => {
            if (
              drawingActiveRef.current &&
              verticesRef.current.length >= 3
            ) {
              closePolygon();
            }
          });
        }
        vertexMarkersRef.current.push(marker);

        // Enable click-to-close on first-vertex marker only after 4+ points
        const firstMarker = vertexMarkersRef.current[0];
        if (firstMarker && verticesRef.current.length >= 4) {
          firstMarker.setOptions({ clickable: true });
        }

        onUndoCallbacksRef.current.forEach((cb) =>
          cb(verticesRef.current.length),
        );
      };

      clickListenerRef.current = map.addListener("click", (e: any) => {
        if (!drawingActiveRef.current) return;
        if (
          verticesRef.current.length >= 3 &&
          isNearFirstVertex(map, e.latLng, verticesRef.current[0])
        ) {
          closePolygon();
          return;
        }
        addVertex(e.latLng);
      });

      dblClickListenerRef.current = map.addListener("dblclick", (e: any) => {
        if (!drawingActiveRef.current) return;
        e.stop();
        if (verticesRef.current.length > 3) {
          const m = vertexMarkersRef.current.pop();
          if (m) m.setMap(null);
          verticesRef.current.pop();
        }
        closePolygon();
      });

      mouseMoveListenerRef.current = map.addListener("mousemove", (e: any) => {
        if (!drawingActiveRef.current || verticesRef.current.length === 0) {
          return;
        }
        if (cursorLineRef.current) {
          cursorLineRef.current.setPath([
            verticesRef.current[verticesRef.current.length - 1],
            e.latLng,
          ]);
        }
        if (closingLineRef.current && verticesRef.current.length >= 2) {
          closingLineRef.current.setPath([e.latLng, verticesRef.current[0]]);
        }
      });

      keyListenerRef.current = (e: KeyboardEvent) => {
        if (!drawingActiveRef.current) return;
        if (e.key === "Backspace" || e.key === "Delete") {
          e.preventDefault();
          undoLastVertex();
        } else if (e.key === "Escape") {
          cleanupDrawing();
          onUndoCallbacksRef.current.forEach((cb) => cb(-1));
        } else if (e.key === "Enter" && verticesRef.current.length >= 3) {
          e.preventDefault();
          closePolygon();
        }
      };
      document.addEventListener("keydown", keyListenerRef.current);
    },
    [mapInstanceRef, closePolygon, undoLastVertex, cleanupDrawing],
  );

  const stopDrawing = useCallback(() => cleanupDrawing(), [cleanupDrawing]);

  const onUndo = useCallback((cb: (count: number) => void) => {
    onUndoCallbacksRef.current.push(cb);
    return () => {
      onUndoCallbacksRef.current = onUndoCallbacksRef.current.filter(
        (c) => c !== cb,
      );
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupDrawing();
  }, [cleanupDrawing]);

  return {
    startDrawing,
    stopDrawing,
    undoLastVertex,
    drawingActiveRef,
    onUndo,
  };
}
